
import React from "react";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import CountryFlag from "@/components/country/CountryFlag";
import { getDocumentTypeName, getCountryName, getDocumentPurposeLabel } from "../utils/documentTypeUtils";
import { IDDocumentType } from "@/hooks/admin/identity-docs/useIDTypes";
import { isFileReference } from "@/utils/temporaryFileStorage";

interface UploadedDocumentsListProps {
  documents: any[];
  idTypes: IDDocumentType[] | undefined;
  countries: any[];
}

export const UploadedDocumentsList: React.FC<UploadedDocumentsListProps> = ({
  documents,
  idTypes,
  countries,
}) => {
  if (!documents || documents.length === 0) return null;

  // Helper to display filename
  const getFileName = (doc: any) => {
    if (!doc.document_file) return "Document file";
    
    if (isFileReference(doc.document_file)) {
      return doc.document_file.name;
    } else if (doc.document_file instanceof FileList && doc.document_file[0]) {
      return doc.document_file[0].name;
    } else if (doc.document_file[0] && doc.document_file[0].name) {
      return doc.document_file[0].name;
    }
    
    return "Document file";
  };

  return (
    <div className="mt-8">
      <h3 className="text-lg font-semibold mb-4">
        Currently Uploaded Documents ({documents.length})
      </h3>
      <div className="space-y-4">
        {documents.map((doc: any, index: number) => (
          <Card key={index}>
            <CardHeader className="p-4">
              <div className="flex justify-between items-center">
                <CardTitle className="text-base flex items-center gap-2">
                  <CountryFlag
                    countryCode={doc.country_code}
                    className="h-4 w-6"
                  />
                  <span>
                    {getCountryName(doc.country_code, countries)} -{" "}
                    {getDocumentTypeName(doc.document_type_id, idTypes)}
                  </span>
                  <span
                    className="ml-2 text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-100"
                  >
                    {getDocumentPurposeLabel(doc.verification_purpose)}
                  </span>
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-4 pt-0 text-sm">
              <p>
                <strong>Document Number:</strong> {doc.document_number}
              </p>
              {doc.issue_date && (
                <p>
                  <strong>Issued:</strong>{" "}
                  {format(new Date(doc.issue_date), "PPP")}
                </p>
              )}
              {doc.expiry_date && (
                <p>
                  <strong>Expires:</strong>{" "}
                  {format(new Date(doc.expiry_date), "PPP")}
                </p>
              )}
              {doc.document_file && (
                <p className="text-xs text-muted-foreground mt-2">
                  File: {getFileName(doc)}
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
